import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Select from './select';
import UtilityButton from '../../buttons/utilityButton';
import PlusIcon from '../../atoms/icons/plus';
import { StoryblokComponent } from 'gatsby-source-storyblok';
import { useBookingConfiguration } from '../../../hooks/useBookingConfiguration';

const RoomSelector = ({
  showRooms,
  setShowRooms,
  selectBoxClasses,
  rooms,
  setRooms,
  setUserInteraction,
}) => {
  const { t } = useTranslation('booking');
  const { content } = useBookingConfiguration();
  const confContent = JSON.parse(content);
  const groupInfoBlock = confContent.group_info;

  const childrenBoxLengthClasses = [
    'w-full',
    'w-1/2',
    'w-1/3',
    'w-1/4',
    'w-1/5',
  ];

  const infantAges = [0, 1];
  const childAges = [...Array(16).keys()].map((i) => i + 2);

  const addRoom = () => {
    setRooms([...rooms, { adults: 2, children: 0, childrenAges: [] }]);
    setUserInteraction(true);
  };

  const removeRoom = (index) => {
    let _rooms = rooms;
    _rooms.splice(index, 1);

    setRooms([..._rooms]);
    setUserInteraction(true);
  };

  const updateRoom = (index, field, value, childIndex) => {
    if (childIndex !== undefined) {
      //change of child age
      rooms[index].childrenAges[childIndex] = parseInt(value);
    } else {
      if (field === 'children') {
        //if change in number of children, add or remove ages to match no of children
        if (rooms[index].childrenAges.length > value) {
          rooms[index].childrenAges.splice(
            value - rooms[index].childrenAges.length,
          );
        }
        for (let i = 0; i < value; i++) {
          if (!rooms[index].childrenAges[i]) {
            rooms[index].childrenAges[i] = 8;
          }
        }
      }

      rooms[index][field] = parseInt(value);
    }
    setUserInteraction(true);
    setRooms([...rooms]);
  };

  useEffect(() => {
    const num = rooms.reduce((acc, room) => {
      return acc + room.adults + room.children;
    }, 0);

    if (num > 9) {
      setShowGroupInfo(true);
    } else {
      setShowGroupInfo(false);
    }
  }, [rooms]);

  const [showGroupInfo, setShowGroupInfo] = useState(false);

  return (
    <div
      className={`${
        showRooms ? 'flex' : 'hidden'
      } fixed inset-0 bg-black bg-opacity-20 transition-opacity z-70`}
      onClick={() => setShowRooms(!showRooms)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          setShowRooms(!showRooms);
        }
      }}
    >
      {/*  Room selector module */}
      <div className="w-full max-w-screen-lg mx-auto relative">
        {showGroupInfo === false && (
          <div
            className={`${
              showRooms ? '' : 'inset-y-full'
            } flex flex-wrap items-center w-full absolute bg-yellow rounded-2xl z-60 top-1/2 p-4 max-w-screen-sm lg:max-w-screen-xs left-1/2 -translate-x-1/2 -translate-y-1/2 transition-[inset] max-h-[90vh] lg:max-h-[calc(100vh-9rem)]`}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className="w-full flex flex-wrap pb-4"
              style={{
                boxShadow: '0px 12px 12px -12px rgba(34, 34, 34, 0.2)',
              }}
            >
              <div className="w-full flex justify-between items-center">
                <h3 className="w-1/2 flex items-center p-0">
                  {t('Rooms and travelers')}
                </h3>
                <UtilityButton
                  label={t('Add room')}
                  onClick={(e) => {
                    addRoom();

                    const element =
                      window.document.getElementById('roomconfig');
                    element.scrollTop = element.innerHeight + 200;
                  }}
                  icon={<PlusIcon inverted={false} />}
                />
              </div>
            </div>
            <div
              className="w-full overflow-y-auto no-scrollbar  max-h-[60vh] lg:max-h-[50vh] divide-y divide-solid divide-black-50"
              id="roomconfig"
            >
              {rooms.map((room, index) => (
                <div key={index} className="w-full py-4 mb-2">
                  <div className="flex justify-between items-center pb-1 pt-4">
                    <span className="text-base font-crystalCondensed ">{`${t(
                      'Room',
                    )} ${index + 1}`}</span>
                    {rooms.length > 1 && (
                      <UtilityButton
                        onClick={() => removeRoom(index)}
                        icon={<PlusIcon inverted={true} />}
                        label={t('Remove room')}
                      />
                    )}
                  </div>
                  <div className="flex flex-nowrap w-full">
                    <div className="w-full mr-[1px]">
                      <Select
                        label={t('Adults')}
                        className={`rounded-l-lg mb-[1px] w-full relative `}
                        key={`${index}-a`}
                      >
                        <select
                          id="adults"
                          className={`bg-transparent ${selectBoxClasses}`}
                          onChange={(e) => {
                            updateRoom(index, 'adults', e.target.value);
                          }}
                          value={room.adults}
                        >
                          {[...Array(10).keys()].map((i) => (
                            <option
                              defaultValue={i === room.adults}
                              key={i}
                              value={i}
                            >
                              {i === 0 && t('No adults')}
                              {i === 1 && `${i} ${t('adult')}`}
                              {i > 1 && `${i} ${t('adults')}`}
                            </option>
                          ))}
                        </select>
                      </Select>
                    </div>
                    <div className="w-full">
                      <Select
                        label={t('Children Ages')}
                        className={`rounded-r-lg mb-[1px] w-full relative `}
                        key={`${index}-c`}
                      >
                        <select
                          id="children"
                          className={`bg-transparent ${selectBoxClasses}`}
                          onChange={(e) => {
                            updateRoom(index, 'children', e.target.value);
                          }}
                          value={room.children}
                        >
                          {[...Array(10).keys()].map((i) => (
                            <option
                              defaultValue={room.children}
                              key={i}
                              value={i}
                            >
                              {i === 0 && t('No children')}
                              {i === 1 && `1 ${t('child')}`}
                              {i > 1 && i + ` ${t('children')} `}
                            </option>
                          ))}
                        </select>
                      </Select>
                    </div>
                  </div>
                  <div className="flex flex-col w-full pr-2">
                    {room.children > 0 && (
                      <span className="w-full text-xs font-crystalCondensed mt-2">
                        {t('Age children')}
                      </span>
                    )}
                    {room.children > 0 && (
                      <div className="flex flex-nowrap w-full ">
                        {[...Array(parseInt(room.children))].map((c, ix) => (
                          <Select
                            className={`${
                              childrenBoxLengthClasses[room.children - 1]
                            } ${(c, ix)} ${ix === 0 && 'rounded-l-lg'} ${
                              ix === room.children - 1 && 'rounded-r-lg'
                            } mr-[1px] relative`}
                            label={t('Child nr', { nr: ix + 1 })}
                            key={ix + 'child'}
                          >
                            <select
                              type="number"
                              key={'child-' + ix}
                              className={selectBoxClasses}
                              value={room.childrenAges[ix]}
                              defaultValue={8}
                              onChange={(e) => {
                                updateRoom(
                                  index,
                                  'childrenAges',
                                  e.target.value,
                                  ix,
                                );
                              }}
                            >
                              {[
                                ...(room.adults > 0 ? infantAges : []),
                                ...childAges,
                              ].map((x) => (
                                <option
                                  key={'age-' + ix + x}
                                  value={x}
                                  selected={x === rooms[index].childrenAges[ix]}
                                  defaultValue={8}
                                >
                                  {x} {t('years')}
                                </option>
                              ))}
                            </select>
                          </Select>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
              <div className="flex w-full justify-between items-center py-4">
                <a href="#" onClick={() => setShowGroupInfo(true)}>
                  {t('More than 9 travelers?')}
                </a>
              </div>
            </div>
            <div className="flex w-full">
              <div
                className="w-full justify-start flex pt-4"
                style={{
                  boxShadow: '0px -12px 12px -12px rgba(34, 34, 34, 0.2)',
                }}
              >
                <button
                  className="bg-black text-white rounded-full py-2 px-4 w-full lg:w-auto whitespace-nowrap text-base font-crystalCondensed"
                  onClick={() => setShowRooms(!showRooms)}
                >
                  {t('Close')}
                </button>
              </div>
            </div>
          </div>
        )}
        {showGroupInfo && (
          <div
            className={`${
              showRooms ? '' : 'inset-y-full'
            }  flex flex-wrap items-center w-full absolute bg-yellow rounded-2xl z-60 top-1/2 p-4 max-w-screen-sm lg:max-w-screen-xs left-1/2 -translate-x-1/2 -translate-y-1/2 transition-[inset] max-h-[90vh] lg:max-h-[calc(100vh-9rem)]`}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className="w-full flex flex-wrap pb-4"
              style={{
                boxShadow: '0px 12px 12px -12px rgba(34, 34, 34, 0.2)',
              }}
            >
              <div className="w-full flex justify-between items-center">
                <h3 className="w-1/2 flex items-center p-0">
                  {t('Rooms and travelers')}
                </h3>
                <button
                  onClick={() => {
                    setShowGroupInfo(false);
                  }}
                  className="bg-yellow-60 rounded-full px-2 py-2 rotate-45"
                >
                  <PlusIcon />
                </button>
              </div>
            </div>
            <div className="w-full " id="roomconfig">
              <div className="w-full">
                {groupInfoBlock.map((blok) => {
                  console.log(blok);
                  return (
                    <div
                      className={`${
                        blok.component === 'text'
                          ? '-mx-4 md:-mx-8'
                          : '-mx-2 md:-mx-4'
                      } `}
                    >
                      <StoryblokComponent blok={blok} />
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RoomSelector;
